import { createContext, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { getAuth0Logout } from "@/lib/authInterceptor";
import { track } from "@/lib/track";
import i18n, { SUPPORTED_LOCALES } from "@/i18n";

const AuthContext = createContext(null);
const ADMIN_TOKEN_KEY = "erio_admin_token";
const IMPERSONATOR_KEY = "erio_impersonator";

/**
 * Apply the tenant's default locale to i18n on first authenticated load,
 * BUT only when:
 *   - the user has not made an explicit personal choice (no
 *     `erio_locale` in localStorage), and
 *   - the URL does not already pin a locale (we never overrule a
 *     shared `/fr/...` link).
 * This honours the precedence chain agreed in iter156/iter159:
 *   URL  >  user preference  >  tenant default  >  navigator  >  EN.
 */
function _applyTenantLocale(userData) {
  const loc = (userData && userData.tenant_default_locale) || null;
  if (!loc) return;
  const supported = SUPPORTED_LOCALES.map((l) => l.code);
  if (!supported.includes(loc)) return;
  try {
    if (localStorage.getItem("erio_locale")) return;
  } catch { /* private mode */ }
  try {
    const urlPrefix = window.location.pathname.split("/").filter(Boolean)[0];
    if (urlPrefix && supported.includes(urlPrefix)) return;
  } catch { /* SSR */ }
  if ((i18n.language || "").slice(0, 2) !== loc) {
    i18n.changeLanguage(loc);
  }
}

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(() =>
    localStorage.getItem(TOKEN_KEY) ? null : false
  );
  const [impersonator, setImpersonator] = useState(() =>
    localStorage.getItem(IMPERSONATOR_KEY) || null
  );

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) return undefined;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return undefined;
    let active = true;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (active) {
          setUser(data);
          _applyTenantLocale(data);
        }
      })
      .catch((err) => {
        // Bootstrap /auth/me failed. If it's a 401, the localStorage
        // token is stale (session-expired). Set the breadcrumb +
        // resume-path so the next /login render shows the warning
        // toast and bounces back to the user's original destination
        // after re-login. (Path capture is best-effort and skipped on
        // public routes that don't make sense to resume to.)
        const status = err?.response?.status;
        if (status === 401) {
          try {
            const here = window.location.pathname + window.location.search + window.location.hash;
            const PUBLIC_PATHS = ["/", "/login", "/signup", "/logout", "/request-access",
              "/how-it-works", "/privacy", "/trust", "/brief", "/demo", "/pilot",
              "/maturity-assessment", "/risk-health-check", "/plan-health-check",
              "/supplier-risk-lens", "/status", "/benchmarks", "/terms", "/changelog"];
            if (here && !PUBLIC_PATHS.some((p) => here === p || here.startsWith(p + "?") || here.startsWith(p + "#"))) {
              sessionStorage.setItem("erio_post_login_redirect", here);
            }
            sessionStorage.setItem("erio_session_expired", "1");
          } catch (_e) { /* private mode */ }
        }
        localStorage.removeItem(TOKEN_KEY);
        if (active) setUser(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required && !data.token) {
      // Password accepted; a second factor is required before a session is issued.
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    _applyTenantLocale(data.user);
    track("login", { method: "password", role: data.user?.role });
    return data.user;
  };

  // Second factor: exchange the MFA-challenge token + code for an access token.
  const verifyMfaLogin = async (mfaToken, code) => {
    const { data } = await api.post("/auth/mfa/verify-login", {
      mfa_token: mfaToken,
      code,
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    _applyTenantLocale(data.user);
    return data.user;
  };

  // SSO (OIDC): the backend has already issued our app JWT and redirected back
  // with it; store it and hydrate the user exactly like a normal login.
  const loginWithToken = async (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    const { data } = await api.get("/auth/me");
    setUser(data);
    _applyTenantLocale(data);
    track("login", { method: "sso", role: data?.role });
    return data;
  };

  // Owner sign-in via Emergent-managed Google Auth (allowlisted Google account only).
  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", {
      current_password,
      new_password,
    });
    setUser(data.user);
    return data.user;
  };

  // Admin "view as" — preview the app exactly as another user sees it.
  const viewAs = async (userId) => {
    const adminToken = localStorage.getItem(TOKEN_KEY);
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(IMPERSONATOR_KEY, data.impersonator);
    setImpersonator(data.impersonator);
    setUser(data.user);
    return data.user;
  };

  const exitViewAs = async () => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (adminToken) localStorage.setItem(TOKEN_KEY, adminToken);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_KEY);
    setImpersonator(null);
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = () => {
    // iter135 — fire a logout event BEFORE we clear state so any
    // components holding live media (BoardBriefing's <audio>, Ask ERIO
    // narration, NLQ playback) can tear down cleanly. Without this the
    // audio element survives logout because it's a JS-heap object the
    // React tree doesn't own.
    try { window.dispatchEvent(new CustomEvent("erio:logout")); } catch (_e) { /* SSR / old browser */ }
    // If the user signed in via the server-side OIDC SSO flow, hand off to
    // /api/auth/oidc/logout for a *full* sign-out: it clears the shared
    // `.ekselens.com` cookies AND ends the Auth0 session via RP-initiated
    // logout, then bounces the browser back to ERIO /login. Without this
    // step the next click of "Sign in with SSO" would silently re-issue a
    // token from Auth0's cached session — a surprise on shared machines.
    const isOidcSso = user && user.sso_provider === "auth0";
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_KEY);
    setImpersonator(null);
    setUser(false);
    // Drop a sessionStorage breadcrumb BEFORE the cross-domain Auth0
    // round-trip so the "You have been signed out" toast still fires
    // when the browser eventually lands back on /login. The previous
    // approach relied on appending `?signed_out=1` to the Auth0
    // `returnTo`, which forced us to register every querystring
    // variant in Auth0's Allowed Logout URLs — too brittle.
    try { sessionStorage.setItem("erio_signed_out", "1"); } catch (_e) { /* private mode */ }
    if (isOidcSso) {
      // Backend URL (NOT window.location.origin — this endpoint is the
      // FastAPI logout handler, hosted on the backend). Fail fast if
      // REACT_APP_BACKEND_URL is missing rather than silently rerouting
      // to the frontend origin where /api/auth/oidc/logout 404s.
      const base = process.env.REACT_APP_BACKEND_URL;
      const returnTo = encodeURIComponent(`${window.location.origin}/login`);
      window.location.replace(`${base}/api/auth/oidc/logout?return_to=${returnTo}`);
      return;
    }
    // Best-effort: clear the cross-property SSO-signal cookies on
    // `.ekselens.com` so the sister marketing site stops showing
    // "Welcome back". No-op on preview / dev hosts.
    api.post("/auth/sso-cookies/clear").catch(() => {});
    // Legacy Auth0 SPA SDK fallback (kept for any users still using the
    // older client-side Auth0 wrapper — additive, no harm if absent).
    const auth0Logout = getAuth0Logout();
    if (auth0Logout) {
      try { auth0Logout(); } catch (_e) { /* non-fatal */ }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        impersonator,
        login,
        verifyMfaLogin,
        loginWithToken,
        loginWithGoogleSession,
        logout,
        changePassword,
        viewAs,
        exitViewAs,
        setUser,
        refreshUser,
        formatApiErrorDetail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
